import i18next from 'i18next';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { addError } from 'actions/Error';
import { deletePayment, deletePayments } from 'actions/Payments';
import { getAllPayments, getCardPayments } from 'reducers/Payments';
import { RootState } from 'reducers';
import { printPatchscript } from 'actions/integrations/printer';
import { PatchScript } from 'containers/Forms/Settings/views/Debug/components/ReceiptSchema/Editor/schema/comms';

type Payments = ReturnType<typeof getAllPayments>;
type CardPayment = ReturnType<typeof getCardPayments>[number];

export const getTipPaymentKeysForGivenRefNo = (
  payments: Payments,
  refNo?: string,
): string[] => {
  if (!refNo) return [];
  const isTipPayment = card =>
    card.attributes?.cardIsTip || card.type === 'TIP';
  return payments
    .filter(p => isTipPayment(p) && p.attributes?.refNo === refNo)
    .map(p => p.key);
};

export const deletePaymentGroup = (payment: CardPayment) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const allPayments = getAllPayments(getState());

  if (!payment) {
    dispatch(addError(i18next.t('payment:alerts.noPaymentFound')));
    return;
  }

  const hasTip = Number(payment.tipAmount ?? 0) > 0;
  if (!hasTip) {
    dispatch(deletePayment({ key: payment.key }));
    return;
  }

  const tipPaymentKeys = getTipPaymentKeysForGivenRefNo(
    allPayments,
    payment.attributes?.refNo,
  );
  if (tipPaymentKeys.length > 0) {
    dispatch(deletePayments({ keys: [payment.key, ...tipPaymentKeys] }));
    return;
  }

  dispatch(deletePayment({ key: payment.key }));
};

type AdditionalData = {
  cardholder?: string | PatchScript;
  merchant?: string | PatchScript;
  amountDetails?: {
    userTipAmount?: string;
  };
};

export function generatePatchscriptFromString(str: string): PatchScript {
  return str.split('\n').map(text => ({
    align: 'center',
    pieces: [{ text: text.trim() }],
    type: 'text',
  }));
}

export function printMerchantCardholderReceipts(receipts: AdditionalData) {
  return (dispatch: ThunkDispatch<RootState, unknown, Action>) => {
    const { cardholder, merchant } = receipts;
    function getPatchscript(receipt: AdditionalData['cardholder']) {
      if (!receipt) {
        return null;
      }

      if (typeof receipt === 'string') {
        return generatePatchscriptFromString(receipt);
      }

      return receipt;
    }

    const merchantPatchscript = getPatchscript(merchant);

    const cardholderPatchscript = getPatchscript(cardholder);

    if (cardholderPatchscript) {
      dispatch(printPatchscript(cardholderPatchscript));
    }
    if (merchantPatchscript) {
      dispatch(printPatchscript(merchantPatchscript));
    }
  };
}
